import { PageContainer } from '../../shared/PageContainer';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { ScheduleActions } from './ScheduleActions';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { scheduleState } from '../../../redux/schedule/scheduleSlice';
import { LoadingBackground } from '../../shared/LoadingBackground';
import { scheduleRoutes, scheduleRoutesCombined } from '../ScheduleNavigation';
import { useTheme } from '@mui/material';
import { ScheduleMeetingItem } from './ScheduleMeetingItem';
import { ScheduleEventItem } from './ScheduleEventItem';
import { setListExpanded } from '../../../redux/common/commonActions';
import { getSearchCategory, getSearchDate, getLoadingEvent, getMeetingsToDisplay, scheduleParamKeys, updateSearchParamsForCategory, updateSearchParamsForDate } from './schedulePageHelper';
import { loadSideMenuData } from '../../../redux/schedule/scheduleActions';
import { rdsMeeting_Dto } from '../../../interfaces/dtos/rdsMeeting';
import { rdsEvent_Dto } from '../../../interfaces/dtos/rdsEvent';
import { rdsState } from '../../../redux/rds/rdsSlice';
import { categoryItems } from '../../shared/sharedHelper';
import { loadRdsEvents } from '../../../redux/rds/rdsActionsEvent';
import { GroupKeyType } from '@sis/react-library/ListContainer/ListContainer';
import { ListAndDetail } from '@sis/react-library';
import { loadRdsEventStatusesFromMeeting } from '../../../redux/rds/rdsActionsEventStatus';

export interface ScheduleEventMeetingItemDto extends rdsEvent_Dto {
    meetingDescription: string;
    country?: string;
    meetingStatus?: string;
    eventStatus?: string;
}

export const SchedulePage = () => {

    const schedule = useAppSelector(scheduleState);
    const rds = useAppSelector(rdsState);
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const location = useLocation();
    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();

    const searchDate = getSearchDate(searchParams);
    const searchCategory = getSearchCategory(searchParams);

    const isRootPath = location.pathname === scheduleRoutes.root || location.pathname === scheduleRoutesCombined.main;

    useEffect(() => {
        if (isRootPath && searchDate && searchCategory) {
            dispatch(loadSideMenuData(
                searchDate,
                searchCategory
            ));
        }
        // only use the conditions below
        // eslint-disable-next-line
    }, [searchParams.get(scheduleParamKeys.date), searchParams.get(scheduleParamKeys.category)])

    useEffect(() => {
        if (location.pathname === scheduleRoutes.root) {
            navigate(scheduleRoutesCombined.main, { replace: true });
        }
        if (isRootPath && !searchDate || !searchCategory) {
            let newSearchParams = searchParams;
            if (!searchDate) {
                newSearchParams = updateSearchParamsForDate(newSearchParams, new Date());
            }
            if (!searchCategory) {
                newSearchParams = updateSearchParamsForCategory(searchParams, categoryItems[0].key);
            }
            setSearchParams(newSearchParams);
        }
    }, [searchParams, isRootPath, location.pathname, searchDate, searchCategory, navigate, setSearchParams])

    const meetingsToDisplay = getMeetingsToDisplay(rds, searchParams);

    const mapMeeting = (meeting: rdsMeeting_Dto, event: rdsEvent_Dto): ScheduleEventMeetingItemDto => {
        const track = rds.rdsTracks.find((t) => t.id === meeting.trackId);
        const eventStatus = rds.rdsEventStatuses.find(es => es.id === event.id);
        return {
            meetingDescription: meeting.description,
            country: track ? track.country : undefined,
            meetingStatus: meeting.status,
            eventStatus: eventStatus ? eventStatus.status : undefined,
            ...event
        }
    }

    const groupedEvents: ScheduleEventMeetingItemDto[] = meetingsToDisplay.flatMap((m) => {
        const events = rds.rdsEvents.filter((e) => e.meetingId === m.id);
        const sortedEvents = events.toSorted((a, b) => ((a.dateTime < b.dateTime ? -1 : 1)));
        const eventWithMeetingDetails = sortedEvents.map((e) => { return mapMeeting(m, e) });
        return sortedEvents.length === 0 ? [mapMeeting(m, getLoadingEvent(m))] : eventWithMeetingDetails
    });

    const showHeading = isRootPath;

    const handleExpandedCollaped = (expanded: boolean) => {
        dispatch(setListExpanded(expanded));
    }

    const handleGroupExpandedCollapsed = async (meetingId: GroupKeyType, expanded: boolean) => {
        if (expanded) {
            const events = rds.rdsEvents.filter((e) => e.meetingId === meetingId);
            if (events.length === 0) {
                dispatch(loadRdsEvents(meetingId as number));
            }
            globalThis.setTimeout(() => {
                dispatch(loadRdsEventStatusesFromMeeting(meetingId as number));
            }, 200); // Delay a little to load the event statuses so the frame opens smoothly
        }
    }

    const isDerivsPage = location.pathname.includes('/derivs/');

    const listItemRender = (item: ScheduleEventMeetingItemDto) => <ScheduleEventItem eventMeeting={item} />;
    const listGroupRender = (meetingId: GroupKeyType) => <ScheduleMeetingItem meetingId={meetingId} eventMeetings={groupedEvents} />;

    return (
        <PageContainer
            heading='Schedule'
            noHeader={!showHeading}
            icon={<ScheduleIcon />}
            rightElement={
                <ScheduleActions />
            }
        >
            {schedule.loadingSideMenu &&
                <LoadingBackground top={100} left={100} />
            }
            <ListAndDetail
                items={groupedEvents}
                keyValue={(item) => item.id}
                itemRender={listItemRender}
                searchFields={['meetingDescription', 'name']}
                listUrl={scheduleRoutes.root}
                editUrl={isDerivsPage ? scheduleRoutes.derivs : scheduleRoutes.win}
                additionalUrlsToCheckForSelecting={[scheduleRoutes.win, scheduleRoutes.derivs]}
                colours={{
                    background: 'linear-gradient(to right, rgb(90, 90, 90), rgb(104, 104, 104))',
                    colour: 'white',
                    borderColour: theme.palette.grey[700],
                    noItemsColour: theme.palette.grey[400],
                    itemColour: () => theme.palette.common.black,
                    itemBackground: () => theme.palette.common.white,
                    itemSelectedBackground: () => theme.palette.primary.light
                }}
                grouping={{
                    groupKey: 'meetingId',
                    groupColour: () => theme.palette.common.white,
                    groupRender: listGroupRender,
                    groupStartCollapsed: true,
                    onExpandCollapse: handleGroupExpandedCollapsed
                }}
                positionPersistLeft
                pageSize={10000}
                onExpandedCollapsed={(expanded) => handleExpandedCollaped(expanded)}

                reduceHeight={showHeading ? 174 : 116}
            />
        </PageContainer>
    )
}
